
import { supabase } from './supabase';

export const api = {
  // Billing endpoints
  get: async (endpoint: string) => {
    if (endpoint === '/billing/invoices') {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data };
    }

    if (endpoint.startsWith('/billing/invoices/')) {
      const id = endpoint.split('/').pop();
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return { data };
    }

    // Vendor endpoints
    if (endpoint === '/vendor/list') {
      // Mock vendor data since no vendor table exists
      const vendors = [
        { id: '1', name: 'Golden Crafts Ltd', status: 'active', contact: '+91-9876543210', email: 'contact@goldencraft.com' },
        { id: '2', name: 'Diamond Palace', status: 'active', contact: '+91-9876543211', email: 'info@diamondpalace.com' },
        { id: '3', name: 'Silver Works Co', status: 'pending', contact: '+91-9876543212', email: 'sales@silverworks.com' }
      ];
      return { data: vendors };
    }

    if (endpoint.startsWith('/vendor/')) {
      const id = endpoint.split('/')[2];
      const vendor = { id, name: 'Golden Crafts Ltd', status: 'active', contact: '+91-9876543210', email: 'contact@goldencraft.com' };
      return { data: vendor };
    }

    // Warranty endpoints
    if (endpoint === '/warranty/claims') {
      const claims = [
        { id: '1', claimNo: 'WCL-2024-001', productName: 'Gold Ring 18K', customerName: 'Rajesh Kumar', status: 'submitted', claimDate: '2024-01-15' },
        { id: '2', claimNo: 'WCL-2024-002', productName: 'Diamond Necklace', customerName: 'Priya Sharma', status: 'approved', claimDate: '2024-01-14' }
      ];
      return { data: claims };
    }

    if (endpoint === '/warranty/repairs') {
      const repairs = [
        { id: '1', orderNo: 'RPR-2024-001', productName: 'Gold Ring 18K', customerName: 'Rajesh Kumar', status: 'in_progress' },
        { id: '2', orderNo: 'RPR-2024-002', productName: 'Silver Bracelet', customerName: 'Amit Patel', status: 'completed' }
      ];
      return { data: repairs };
    }

    if (endpoint === '/warranty/replacements') {
      const replacements = [
        { id: '1', orderNo: 'REP-2024-001', originalProductName: 'Diamond Ring', customerName: 'Vikram Singh', status: 'pending' }
      ];
      return { data: replacements };
    }

    if (endpoint === '/warranty/stats') {
      const stats = {
        totalClaims: 25,
        pendingClaims: 8,
        approvedClaims: 15,
        rejectedClaims: 2,
        avgResolutionTime: 5.2
      };
      return { data: stats };
    }

    // QC endpoints
    if (endpoint === '/qc/orders') {
      const orders = [
        { id: '1', orderNo: 'ORD-2024-001', product: 'Gold Ring 18K', customer: 'Rajesh Kumar', status: 'pending', priority: 'high' },
        { id: '2', orderNo: 'ORD-2024-002', product: 'Diamond Necklace', customer: 'Priya Sharma', status: 'in-progress', priority: 'medium' }
      ];
      return { data: orders };
    }

    throw new Error(`GET ${endpoint} not implemented`);
  },

  post: async (endpoint: string, data: any) => {
    if (endpoint === '/billing/invoices') {
      const { data: result, error } = await supabase
        .from('invoices')
        .insert([{
          ...data,
          invoice_no: `INV-${Date.now()}`,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();
      if (error) throw error;
      return { data: result };
    }

    if (endpoint === '/inventory/transfer') {
      const { data: result, error } = await supabase
        .from('inventory_transfers')
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return { data: result };
    }

    // Vendor endpoints
    if (endpoint === '/vendor/enroll') {
      const vendor = { id: Date.now().toString(), ...data, status: 'pending', createdAt: new Date().toISOString() };
      return { data: vendor };
    }

    // Warranty endpoints
    if (endpoint === '/warranty/claims') {
      const claim = { id: Date.now().toString(), ...data, createdAt: new Date().toISOString() };
      return { data: claim };
    }

    if (endpoint === '/warranty/repairs') {
      const repair = { id: Date.now().toString(), ...data, createdAt: new Date().toISOString() };
      return { data: repair };
    }

    if (endpoint === '/warranty/replacements') {
      const replacement = { id: Date.now().toString(), ...data, createdAt: new Date().toISOString() };
      return { data: replacement };
    }

    // QC endpoints
    if (endpoint.includes('/qc/inspections/') && endpoint.includes('/complete')) {
      return { data: { success: true } };
    }

    if (endpoint === '/qc/checklists') {
      const checklist = { id: Date.now().toString(), ...data, createdAt: new Date().toISOString() };
      return { data: checklist };
    }

    throw new Error(`POST ${endpoint} not implemented`);
  },
  
  put: async (endpoint: string, data: any) => {
    if (endpoint.startsWith('/billing/invoices/')) {
      const id = endpoint.split('/').pop();
      const { data: result, error } = await supabase
        .from('invoices')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return { data: result };
    }

    // Vendor endpoints
    if (endpoint.includes('/vendor/') && endpoint.includes('/status')) {
      return { data: { success: true } };
    }

    if (endpoint.startsWith('/vendor/')) {
      return { data: { success: true } };
    }

    // Warranty endpoints
    if (endpoint.startsWith('/warranty/claims/')) {
      return { data: { success: true } };
    }

    if (endpoint.startsWith('/warranty/repairs/')) {
      return { data: { success: true } };
    }

    // QC endpoints
    if (endpoint.startsWith('/qc/inspections/')) {
      return { data: { success: true } };
    }

    if (endpoint.startsWith('/qc/checklists/')) {
      return { data: { success: true } };
    }

    throw new Error(`PUT ${endpoint} not implemented`);
  },
  
  delete: async (endpoint: string) => {
    if (endpoint.startsWith('/qc/checklists/')) {
      return { data: { success: true } };
    }

    throw new Error(`DELETE ${endpoint} not implemented`);
  }
};